export interface DistributionSegment {
  id: string;
  label: string;
  /** Monto o parte; el ancho es su proporción del total. */
  value: number;
  /** Cualquier color CSS (p. ej. un valor de la paleta de categorías). */
  color: string;
}

export interface DistributionBarProps {
  segments: DistributionSegment[];
  /** Muestra la leyenda bajo la barra (por defecto sí). */
  legend?: boolean;
  /** Resumen accesible de la barra, p. ej. "Comida 42%, Renta 58%". */
  ariaLabel: string;
  /** Añade ■ / ● a la leyenda para que el significado no dependa solo del color. */
  shapes?: boolean;
}

/** Porcentaje entero de un segmento sobre el total (0 si el total es 0). */
const percentOf = (value: number, total: number) => (total > 0 ? Math.round((value / total) * 100) : 0);

/**
 * DistributionBar: barra apilada de 16px con segmentos separados por 4px. Cada segmento mide su % del total;
 * la leyenda en texto muestra nombre y % (y forma opcional), así el color no es el único dato.
 */
export function DistributionBar({ segments, legend = true, ariaLabel, shapes = false }: DistributionBarProps) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);

  return (
    <div>
      <div role="img" aria-label={ariaLabel} className="flex h-4 gap-1">
        {segments.map((s) => (
          <i
            key={s.id}
            className="block min-w-1 rounded-full"
            style={{ flex: `${total > 0 ? s.value / total : 0} 1 0`, background: s.color }}
          />
        ))}
      </div>
      {legend && (
        <ul className="m-0 mt-2 flex list-none flex-wrap gap-x-4 gap-y-2 p-0 text-[13px] leading-[18px] text-[var(--ds-ink)]">
          {segments.map((s, i) => (
            <li key={s.id} className="inline-flex items-center gap-2">
              {shapes ? (
                <span aria-hidden="true" className="text-[12px] leading-none" style={{ color: s.color }}>
                  {i % 2 === 0 ? "■" : "●"}
                </span>
              ) : (
                <span aria-hidden="true" className="size-3 flex-none rounded-[3px]" style={{ background: s.color }} />
              )}
              {s.label} {percentOf(s.value, total)}%
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
